import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import SaveIcon from '@mui/icons-material/Save';
import { adminChatbotApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import type { ChatbotPrompt, ChatbotService, ChatbotSettings } from '../../types';

/** Dòng nhãn đang sửa trong bảng dịch vụ — theo id dịch vụ. */
type EditingLabels = Record<string, string>;
/** Dòng message đang sửa trong bảng prompt — theo step_key. */
type EditingMessages = Record<string, string>;

export function ChatbotManagement(): JSX.Element {
  const [services, setServices] = useState<ChatbotService[]>([]);
  const [prompts, setPrompts] = useState<ChatbotPrompt[]>([]);
  const [settings, setSettings] = useState<ChatbotSettings | null>(null);
  const [maxTurnsInput, setMaxTurnsInput] = useState('');
  const [systemPromptInput, setSystemPromptInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState<EditingLabels>({});
  const [messages, setMessages] = useState<EditingMessages>({});
  const [savingServiceId, setSavingServiceId] = useState<string | null>(null);
  const [savingStepKey, setSavingStepKey] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [svc, pr, st] = await Promise.all([
        adminChatbotApi.listServices(),
        adminChatbotApi.listPrompts(),
        adminChatbotApi.getSettings(),
      ]);
      setServices(svc);
      setPrompts(pr);
      setSettings(st);
      setLabels(Object.fromEntries(svc.map((s) => [s.id, s.label])));
      setMessages(Object.fromEntries(pr.map((p) => [p.step_key, p.message])));
      setMaxTurnsInput(String(st.ai_max_turns));
      setSystemPromptInput(st.ai_system_prompt);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải cấu hình chatbot'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleToggleEnabled = async (svc: ChatbotService) => {
    setSavingServiceId(svc.id);
    try {
      await adminChatbotApi.updateService(svc.id, { enabled: svc.enabled !== 1 });
      notify('Đã cập nhật trạng thái dịch vụ', 'success');
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể cập nhật dịch vụ'), 'error');
    } finally {
      setSavingServiceId(null);
    }
  };

  const handleSaveLabel = async (svc: ChatbotService) => {
    const label = (labels[svc.id] ?? '').trim();
    if (label.length === 0) {
      notify('Nhãn không được để trống', 'warning');
      return;
    }
    setSavingServiceId(svc.id);
    try {
      await adminChatbotApi.updateService(svc.id, { label });
      notify('Đã lưu nhãn dịch vụ', 'success');
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu nhãn'), 'error');
    } finally {
      setSavingServiceId(null);
    }
  };

  const handleToggleAiEnabled = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      await adminChatbotApi.updateSettings({ ai_enabled: settings.ai_enabled !== 1 });
      notify('Đã cập nhật trạng thái AI', 'success');
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể cập nhật trạng thái AI'), 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveAiSettings = async () => {
    const maxTurns = Number(maxTurnsInput);
    if (!Number.isInteger(maxTurns) || maxTurns < 1 || maxTurns > 10) {
      notify('Số lượt tối đa phải là số nguyên từ 1 đến 10', 'warning');
      return;
    }
    if (systemPromptInput.trim().length === 0) {
      notify('System prompt không được để trống', 'warning');
      return;
    }
    setSavingSettings(true);
    try {
      await adminChatbotApi.updateSettings({
        ai_max_turns: maxTurns,
        ai_system_prompt: systemPromptInput.trim(),
      });
      notify('Đã lưu cấu hình AI', 'success');
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu cấu hình AI'), 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveMessage = async (prompt: ChatbotPrompt) => {
    const message = (messages[prompt.step_key] ?? '').trim();
    if (message.length === 0) {
      notify('Nội dung không được để trống', 'warning');
      return;
    }
    setSavingStepKey(prompt.step_key);
    try {
      await adminChatbotApi.updatePrompt(prompt.step_key, message);
      notify('Đã lưu nội dung câu hỏi', 'success');
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu nội dung'), 'error');
    } finally {
      setSavingStepKey(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Dịch vụ chatbot
      </Typography>
      <Paper variant="outlined" sx={{ mb: 4 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Khoá</TableCell>
                <TableCell>Nhãn hiển thị</TableCell>
                <TableCell align="center">Bật</TableCell>
                <TableCell align="right">Lưu</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((svc) => (
                <TableRow key={svc.id} hover>
                  <TableCell>{svc.key}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={labels[svc.id] ?? ''}
                      onChange={(e) => setLabels((prev) => ({ ...prev, [svc.id]: e.target.value }))}
                      disabled={savingServiceId === svc.id}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={svc.enabled === 1}
                      onChange={() => void handleToggleEnabled(svc)}
                      disabled={savingServiceId === svc.id}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Lưu nhãn">
                      <IconButton
                        size="small"
                        onClick={() => void handleSaveLabel(svc)}
                        disabled={savingServiceId === svc.id}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        AI hội thoại tự do (v2)
      </Typography>
      <Paper variant="outlined" sx={{ mb: 4, p: 2 }}>
        {!settings?.ai_enabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Đang TẮT — user chỉ thấy luồng hỏi-từng-bước, không có nút "Tôi tự mô tả". Chỉ bật khi đã đặt
            `ANTHROPIC_API_KEY` thật qua <code>wrangler secret put</code> trên môi trường tương ứng.
          </Alert>
        )}
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={settings?.ai_enabled === 1}
                onChange={() => void handleToggleAiEnabled()}
                disabled={savingSettings || !settings}
              />
            }
            label="Bật AI hội thoại tự do"
          />
          <TextField
            label="Số lượt AI tối đa mỗi cuộc chat"
            size="small"
            type="number"
            value={maxTurnsInput}
            onChange={(e) => setMaxTurnsInput(e.target.value)}
            disabled={savingSettings}
            inputProps={{ min: 1, max: 10 }}
            sx={{ maxWidth: 240 }}
          />
          <TextField
            label="System prompt (hướng dẫn nội bộ cho AI — không hiện cho user)"
            size="small"
            fullWidth
            multiline
            minRows={6}
            value={systemPromptInput}
            onChange={(e) => setSystemPromptInput(e.target.value)}
            disabled={savingSettings}
          />
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => void handleSaveAiSettings()}
              disabled={savingSettings}
              startIcon={savingSettings ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
            >
              Lưu số lượt + system prompt
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Nội dung câu hỏi
      </Typography>
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bước</TableCell>
                <TableCell>Nội dung</TableCell>
                <TableCell align="right">Lưu</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prompts.map((prompt) => (
                <TableRow key={prompt.step_key} hover>
                  <TableCell>{prompt.step_key}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      value={messages[prompt.step_key] ?? ''}
                      onChange={(e) =>
                        setMessages((prev) => ({ ...prev, [prompt.step_key]: e.target.value }))
                      }
                      disabled={savingStepKey === prompt.step_key}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Lưu nội dung">
                      <IconButton
                        size="small"
                        onClick={() => void handleSaveMessage(prompt)}
                        disabled={savingStepKey === prompt.step_key}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
